import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { X, Loader2, Trash2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SIGNAL_LABELS = {
  stale_estimates: "Stale estimates",
  stale_leads: "Stale leads",
  overdue_invoices: "Overdue invoices",
};

export default function CoachBulkDraftDrawer({ signal = "stale_estimates", onClose, onDone }) {
  const [channel, setChannel] = useState("email");
  const [drafts, setDrafts] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/business-coach/actions/bulk-draft",
        { signal, channel, limit: 5 });
      setBatchId(data.batch_id);
      setDrafts(data.drafts);
      if (data.count === 0) toast.info("No matching opportunities right now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to draft batch");
    } finally { setLoading(false); }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const updateDraftLocal = (id, patch) => {
    setDrafts(drafts.map(d => d.id === id ? { ...d, ...patch } : d));
  };

  const saveOne = async (d) => {
    await api.patch(`/business-coach/actions/${d.id}`, {
      subject: d.subject || "", body: d.body || "",
    });
  };

  const approveOne = async (d) => {
    if (d.status === "sent") { toast.info("Already sent"); return; }
    setApprovingId(d.id);
    try {
      await saveOne(d);
      const { data } = await api.post(`/business-coach/actions/${d.id}/approve`);
      updateDraftLocal(d.id, data);
      if (data.status === "sent") toast.success(`Sent to ${data.target_name || "customer"}`);
      else toast.error(`Failed: ${data.reason || "unknown"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setApprovingId(null); }
  };

  const discardOne = async (d) => {
    if (d.status !== "draft") { setDrafts(drafts.filter(x => x.id !== d.id)); return; }
    if (!window.confirm(`Discard the ${d.target_name || "customer"} draft?`)) return;
    try {
      await api.post(`/business-coach/actions/${d.id}/discard`);
      setDrafts(drafts.filter(x => x.id !== d.id));
    } catch (e) { toast.error("Failed to discard"); }
  };

  const pending = drafts.filter(d => d.status === "draft");

  const approveAll = async () => {
    if (pending.length === 0) return;
    const names = pending.map(d => d.target_name || "customer").join(", ");
    if (!window.confirm(`Send ${pending.length} ${channel}(s) to: ${names}?`)) return;
    setApprovingAll(true);
    try {
      for (const d of pending) {
        try {
          await saveOne(d);
          const { data } = await api.post(`/business-coach/actions/${d.id}/approve`);
          updateDraftLocal(d.id, data);
        } catch (e) {
          updateDraftLocal(d.id, { status: "failed", reason: e?.response?.data?.detail || "error" });
        }
      }
      const ok = drafts.filter(d => d.status === "sent").length;
      toast.success(`Batch complete — ${ok} sent, ${pending.length - ok} failed`);
      onDone?.();
    } finally { setApprovingAll(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="coach-bulk-drawer" onClick={onClose}>
      <div className="w-full sm:w-[720px] bg-white h-full overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00]">Bulk drafts</div>
            <div className="font-heading text-xl font-bold">{SIGNAL_LABELS[signal] || signal}</div>
            {batchId && <div className="text-xs text-zinc-500 mt-0.5">
              {drafts.length} draft{drafts.length === 1 ? "" : "s"} · {pending.length} awaiting approval · max 5 per batch
            </div>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-md" data-testid="coach-bulk-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500 uppercase tracking-wider">Channel:</span>
            {["email", "sms"].map((ch) => (
              <button key={ch} onClick={() => setChannel(ch)}
                      disabled={drafts.length > 0}
                      className={`px-2 py-1 rounded-md ${
                        channel === ch ? "bg-zinc-900 text-white" : "bg-zinc-100 hover:bg-zinc-200"
                      } disabled:opacity-50`}>
                {ch.toUpperCase()}
              </button>
            ))}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin"/> Coach is drafting {5} follow-ups…
            </div>
          )}

          {!loading && drafts.length === 0 && (
            <div className="text-sm text-zinc-500" data-testid="coach-bulk-empty">
              No matching opportunities.
            </div>
          )}

          {!loading && drafts.map((d, i) => (
            <Card key={d.id} className="p-4"
                  data-testid={`coach-bulk-draft-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="text-sm font-semibold">{d.target_name || "Customer"}</div>
                  <div className="text-xs text-zinc-500">{d.priority_reason}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {d.channel === "email" ? d.target_email : d.target_phone}
                  </div>
                </div>
                <div>
                  <StatusChip status={d.status}/>
                </div>
              </div>
              {d.channel === "email" && (
                <Input
                  className="mb-2"
                  value={d.subject || ""}
                  onChange={(e) => updateDraftLocal(d.id, { subject: e.target.value })}
                  disabled={d.status !== "draft"}
                  data-testid={`coach-bulk-subject-${i}`}
                />
              )}
              <Textarea
                className="min-h-[140px] text-sm font-mono"
                value={d.body || ""}
                onChange={(e) => updateDraftLocal(d.id, { body: e.target.value })}
                disabled={d.status !== "draft"}
                data-testid={`coach-bulk-body-${i}`}
              />
              {d.status === "draft" && (
                <div className="flex items-center justify-end gap-2 mt-2">
                  <Button variant="outline" size="sm"
                          onClick={() => discardOne(d)}
                          data-testid={`coach-bulk-discard-${i}`}
                          className="text-rose-600">
                    <Trash2 className="w-3 h-3 mr-1"/> Remove
                  </Button>
                  <Button size="sm"
                          disabled={approvingId === d.id || approvingAll}
                          onClick={() => approveOne(d)}
                          data-testid={`coach-bulk-approve-${i}`}
                          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white">
                    {approvingId === d.id ? "Sending…" : <><Send className="w-3 h-3 mr-1"/> Approve &amp; send</>}
                  </Button>
                </div>
              )}
              {d.status === "sent" && (
                <div className="text-xs text-emerald-700 flex items-center gap-1 mt-2">
                  <CheckCircle2 className="w-3 h-3"/> Sent {d.sent_at?.replace("T", " ").slice(0, 16)}
                </div>
              )}
              {d.status === "failed" && (
                <div className="text-xs text-rose-700 mt-2">Failed: {d.reason || "unknown"}</div>
              )}
            </Card>
          ))}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="coach-bulk-cancel">Close</Button>
          <Button
            onClick={approveAll}
            disabled={approvingAll || pending.length === 0}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="coach-bulk-approve-all"
          >
            {approvingAll ? "Sending batch…" : <><Send className="w-4 h-4 mr-1"/> Approve &amp; send all ({pending.length})</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    draft: "bg-orange-100 text-orange-700",
    sent: "bg-emerald-100 text-emerald-700",
    failed: "bg-rose-100 text-rose-700",
    discarded: "bg-zinc-100 text-zinc-600",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${map[status] || "bg-zinc-100 text-zinc-600"}`}>
      {status === "discarded" ? "cancelled" : status}
    </span>
  );
}
